import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { env } from '@/shared/config/env';
import { useAuth } from '@/shared/auth/useAuth';
import { getIntlLocale, useLocale } from '@/shared/i18n';
import { usePermissions } from '@/shared/permissions/usePermissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { PageContainer } from '@/shared/ui/PageContainer';

type Tone = 'success' | 'warning' | 'info' | 'primary' | 'secondary';

interface MetricCardProps {
  label: string;
  value: string;
  helper: string;
  delta: string;
  tone: Tone;
  icon: ReactNode;
}

interface BreakdownItem {
  label: string;
  value: string;
  share: number;
}

interface BreakdownCardProps {
  title: string;
  subtitle: string;
  items: BreakdownItem[];
}

const MOCK_DASHBOARD = {
  revenue: 12_840_000,
  orders: 1842,
  averageOrderValue: 6970,
  expenses: 7_420_000,
  procurementShare: 68,
  grossMargin: 42,
  cashBalance: 5_280_000,
  receivables: 1_140_000,
  payables: 860_000,
  netProfit: 2_760_000,
  activeCustomers: 18_430,
  repeatRate: 37,
  products: 4320,
  lowStock: 126,
  channels: [
    { key: 'telegram', amount: 5_100_000, share: 40 },
    { key: 'web', amount: 4_420_000, share: 34 },
    { key: 'retail', amount: 2_080_000, share: 16 },
    { key: 'max', amount: 1_240_000, share: 10 },
  ],
  expensesByCategory: [
    { key: 'procurement', amount: 5_050_000, share: 68 },
    { key: 'delivery', amount: 890_000, share: 12 },
    { key: 'marketing', amount: 740_000, share: 10 },
    { key: 'operations', amount: 740_000, share: 10 },
  ],
};

function MetricCard({ label, value, helper, delta, tone, icon }: MetricCardProps) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.5 }} noWrap>
              {value}
            </Typography>
          </Box>
          <Box
            aria-hidden
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              color: `${tone}.main`,
              bgcolor: (theme) => alpha(theme.palette[tone].main, 0.12),
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <Chip size="small" color={tone === 'warning' ? 'warning' : 'success'} label={delta} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {helper}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function BreakdownCard({ title, subtitle, items }: BreakdownCardProps) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {items.map((item) => (
            <Box key={item.label}>
              <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.75 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {item.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {item.value}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={item.share}
                aria-label={`${item.label}: ${item.share}%`}
                sx={{ height: 8, borderRadius: 999 }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

export function HomePage() {
  const { t } = useTranslation('app');
  useLocale();
  const { user } = useAuth();
  const { tenantName, tenantId } = useTenant();
  const { permissions } = usePermissions();
  const intlLocale = getIntlLocale();
  const currency = new Intl.NumberFormat(intlLocale, {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  });
  const integer = new Intl.NumberFormat(intlLocale);

  const channelBreakdown = MOCK_DASHBOARD.channels.map((channel) => ({
    label: t(`home.dashboard.channels.${channel.key}`),
    value: currency.format(channel.amount),
    share: channel.share,
  }));
  const expenseBreakdown = MOCK_DASHBOARD.expensesByCategory.map((category) => ({
    label: t(`home.dashboard.expenses.${category.key}`),
    value: currency.format(category.amount),
    share: category.share,
  }));

  return (
    <PageContainer
      title={t('home.title', { appName: env.appName })}
      subtitle={t('home.dashboard.subtitle', {
        tenant: tenantName ?? t('home.dashboard.unknownTenant'),
      })}
      maxWidth={1280}
    >
      <Stack spacing={2.5}>
        <Alert severity="info" variant="outlined">
          {t('home.dashboard.mockNotice')}
        </Alert>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          }}
        >
          <MetricCard
            label={t('home.dashboard.metrics.revenue.label')}
            value={currency.format(MOCK_DASHBOARD.revenue)}
            helper={t('home.dashboard.metrics.revenue.helper', {
              orders: integer.format(MOCK_DASHBOARD.orders),
              aov: currency.format(MOCK_DASHBOARD.averageOrderValue),
            })}
            delta={t('home.dashboard.metrics.revenue.delta')}
            tone="success"
            icon={<PaidRoundedIcon />}
          />
          <MetricCard
            label={t('home.dashboard.metrics.expenses.label')}
            value={currency.format(MOCK_DASHBOARD.expenses)}
            helper={t('home.dashboard.metrics.expenses.helper', {
              procurementShare: MOCK_DASHBOARD.procurementShare,
              margin: MOCK_DASHBOARD.grossMargin,
            })}
            delta={t('home.dashboard.metrics.expenses.delta')}
            tone="warning"
            icon={<TrendingDownRoundedIcon />}
          />
          <MetricCard
            label={t('home.dashboard.metrics.balance.label')}
            value={currency.format(MOCK_DASHBOARD.cashBalance)}
            helper={t('home.dashboard.metrics.balance.helper', {
              receivables: currency.format(MOCK_DASHBOARD.receivables),
              payables: currency.format(MOCK_DASHBOARD.payables),
            })}
            delta={t('home.dashboard.metrics.balance.delta')}
            tone="primary"
            icon={<AccountBalanceWalletRoundedIcon />}
          />
          <MetricCard
            label={t('home.dashboard.metrics.profit.label')}
            value={currency.format(MOCK_DASHBOARD.netProfit)}
            helper={t('home.dashboard.metrics.profit.helper')}
            delta={t('home.dashboard.metrics.profit.delta')}
            tone="secondary"
            icon={<TrendingUpRoundedIcon />}
          />
          <MetricCard
            label={t('home.dashboard.metrics.customers.label')}
            value={integer.format(MOCK_DASHBOARD.activeCustomers)}
            helper={t('home.dashboard.metrics.customers.helper', {
              repeatRate: MOCK_DASHBOARD.repeatRate,
            })}
            delta={t('home.dashboard.metrics.customers.delta')}
            tone="info"
            icon={<GroupsRoundedIcon />}
          />
          <MetricCard
            label={t('home.dashboard.metrics.products.label')}
            value={integer.format(MOCK_DASHBOARD.products)}
            helper={t('home.dashboard.metrics.products.helper', {
              lowStock: integer.format(MOCK_DASHBOARD.lowStock),
            })}
            delta={t('home.dashboard.metrics.products.delta')}
            tone="primary"
            icon={<Inventory2RoundedIcon />}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
          }}
        >
          <BreakdownCard
            title={t('home.dashboard.salesChannels.title')}
            subtitle={t('home.dashboard.salesChannels.subtitle')}
            items={channelBreakdown}
          />
          <BreakdownCard
            title={t('home.dashboard.expenseStructure.title')}
            subtitle={t('home.dashboard.expenseStructure.subtitle')}
            items={expenseBreakdown}
          />
        </Box>

        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              divider={<Divider orientation="vertical" flexItem />}
              spacing={2}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('home.activeTenant')}
                </Typography>
                <Typography variant="h6" noWrap>
                  {tenantName ?? t('home.dashboard.unknownTenant')}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {tenantId ?? t('home.dashboard.unknownTenantId')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('home.signedInAs')}
                </Typography>
                <Typography variant="h6" noWrap>
                  {user?.name ?? t('home.dashboard.unknownUser')}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {user?.email ?? t('home.dashboard.unknownUserEmail')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('home.permissionsLoaded')}
                </Typography>
                <Typography variant="h6" noWrap>
                  {t('home.permissionsGranted', { count: permissions.length })}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {t('home.dashboard.permissionsHint')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('home.dashboard.orderFlow.label')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReceiptLongRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="h6" noWrap>
                    {t('home.dashboard.orderFlow.value')}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <ShoppingCartCheckoutRoundedIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                  {t('home.dashboard.orderFlow.helper')}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </PageContainer>
  );
}
